import {
  MutableRefObject,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Autocomplete,
  AutocompleteProps,
  Button,
  Icon,
  Option,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { tanimoto } from '../../utils/tanimoto';
import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

const findLeastOption = <T,>(
  options: Option<T>[],
  getLabel: (option: Option<T>) => string,
  search: string,
): Option<T> | undefined => {
  let result: Option<T> | undefined;
  let max = 0;

  for (const option of options) {
    const label = getLabel(option);
    const current = tanimoto(search, label);
    if (current > max) {
      result = option;
      max = current;
    }
  }

  return result;
};

export interface CollapseSearchProps<T> extends AutocompleteProps<T> {
  tooltipTitle: ReactNode;
  testId?: string;
  onToggle?: (value: boolean) => void;
  alwaysExpanded?: boolean;
  containerRef?: MutableRefObject<HTMLElement | null>;
}

export const CollapseSearch = <T,>({
  tooltipTitle,
  onChange,
  testId,
  options,
  renderValue,
  disabled,
  onToggle,
  alwaysExpanded,
  containerRef,
  ...rest
}: CollapseSearchProps<T>): ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isCollapsedRef = useRef(true);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
    onToggle?.(true);
    setTimeout(() => {
      isCollapsedRef.current = true;
    });
  }, [onToggle]);

  const expand = useCallback(() => {
    setIsCollapsed(false);
    onToggle?.(false);
    setTimeout(() => {
      isCollapsedRef.current = false;
    });
  }, [onToggle]);

  const handleChange = useCallback(
    (option: Option<T>) => {
      onChange(option);
      collapse();
    },
    [collapse, onChange],
  );

  const handleSearch = useCallback(
    (search: string | number | readonly string[] | undefined) => {
      if (typeof search !== 'string') return;

      const option = findLeastOption(options, renderValue, search);
      if (option) handleChange(option);
    },
    [renderValue, handleChange, options],
  );

  const handleOverlayClick = useCallback(
    (e: MouseEvent) => {
      if (isCollapsedRef.current) return;

      const preventedElement = (e.target as HTMLElement).closest(
        '[data-prevented]',
      );
      if (preventedElement) return;

      collapse();
    },
    [collapse],
  );

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    if (alwaysExpanded) return;

    container.addEventListener('click', handleOverlayClick);

    return () => {
      container.removeEventListener('click', handleOverlayClick);
    };
  }, [alwaysExpanded, containerRef, handleOverlayClick, isCollapsed]);

  if (alwaysExpanded)
    return (
      <Autocomplete
        {...(rest as AutocompleteProps<T>)}
        renderValue={renderValue}
        options={options}
        onChange={handleChange}
        onSearch={handleSearch}
        dataTestId={`${testId}Autocomplete`}
        disabled={disabled}
        fullWidth
      />
    );

  return isCollapsed ? (
    <TooltipWrapper title={tooltipTitle} dataTestId={`${testId}Tooltip`}>
      <Button
        size='medium'
        startIcon={<Icon iconName={Icons.Search} />}
        onClick={expand}
        data-testid={testId}
        disabled={disabled}
      />
    </TooltipWrapper>
  ) : (
    <div data-prevented>
      <Autocomplete
        {...(rest as AutocompleteProps<T>)}
        renderValue={renderValue}
        options={options}
        autoFocus
        open
        onChange={handleChange}
        onSearch={handleSearch}
        dataTestId={`${testId}Autocomplete`}
        disabled={disabled}
        fullWidth
      />
    </div>
  );
};
